import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Container } from '@mui/material';
import { useDemo } from '../../contexts/DemoContext';
import { cancelDemoTour, requestDemoTour } from '../../components/demo/DemoTour';
import { DemoPersonaRole } from '../../services/demoService';
import { getDemoRouteForRole } from '../../utils/roleNavigation';
import DemoTopBar from './sections/DemoTopBar';
import DemoHero from './sections/DemoHero';
import PersonaDoors from './sections/PersonaDoors';
import FeatureGrid from './sections/FeatureGrid';
import ProofSection from './sections/ProofSection';
import DemoCta from './sections/DemoCta';
import DemoFooter from './sections/DemoFooter';

/**
 * The front door of salesdemo.mathwizleague.com.
 *
 * A visitor here has usually been sent a link and knows nothing about the product, so the page has
 * two jobs: say what MathWiz League is, and get them inside without a password. The page owns its
 * whole surface — slim top bar, marketing bands, slim footer — rather than borrowing the app's
 * MainLayout chrome, whose login/register navigation contradicts a no-password demo. Copy lives in
 * demoContent.ts; the sections under ./sections are presentational.
 */
export default function DemoLandingPage() {
  const navigate = useNavigate();
  const { startDemo, loading, error } = useDemo();
  const [pending, setPending] = useState<DemoPersonaRole | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // startDemo never rejects — DemoContext catches internally and sets `error`. The alert lives
  // between the hero and the persona doors, so bring it into view no matter which control failed.
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  /**
   * @param withTour when true, requests the guided tour for this entry. Entering through a persona
   *   card withdraws any outstanding request instead — someone who picked a specific role has told
   *   you what they want to look at, and should not be walked somewhere else.
   */
  const open = async (role: DemoPersonaRole, withTour = false) => {
    setPending(role);

    if (withTour) {
      requestDemoTour();
    } else {
      cancelDemoTour();
    }

    try {
      // Only navigate on success. startDemo never rejects — it reports failure by resolving false
      // and setting `error` — and the role routes are behind ProtectedRoute, so navigating after a
      // failed start would land the visitor on the login form of a demo sold on having no login.
      if (await startDemo(role)) {
        navigate(getDemoRouteForRole(role));
      }
    } finally {
      setPending(null);
    }
  };

  return (
    <Box>
      <DemoTopBar onStartTour={() => open('student', true)} loading={loading} />
      <DemoHero onStartTour={() => open('student', true)} loading={loading} />

      {error && (
        <Box ref={errorRef} sx={{ bgcolor: 'background.paper', pt: { xs: 4, md: 5 } }}>
          <Container maxWidth="md">
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Container>
        </Box>
      )}

      <PersonaDoors onOpen={role => open(role)} loading={loading} pending={pending} />
      <FeatureGrid />
      <ProofSection />
      <DemoCta />
      <DemoFooter />
    </Box>
  );
}
